import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { readRefCode, clearRefCode } from '../../lib/referral';
import { SystemBoot } from '../ui/SystemBoot';
import { ArrowRight, Loader2 } from 'lucide-react';
import { getAppUrl } from '../../lib/url';
import { reportError } from '../../lib/errors';

// Forge Workshop brand (see docs/decisions/BRAND.md). Hardcoded here for the brand moment;
// the dashboard-wide --fds-* retheme is a separate tracked pass.
// Midnight & Ivory — the theme variables (styles/theme.css is static, so they resolve
// on first paint, before the vertical manifest loads) — the gate follows the theme.
const C = {
    bg: 'var(--fd-base)', surface: 'var(--fd-surface)',
    border: 'color-mix(in oklab, var(--fd-tint) 12%, transparent)', text: 'var(--fd-text-bright)', dim: 'var(--fd-text-soft)',
    accent: 'var(--fd-gold-fill)', accentInk: 'var(--fd-gold-ink)',
};

/**
 * Option B onboarding gate.
 * Onboarding-complete is derived purely from the DB: does the authenticated
 * user have an organization_members row? No localStorage bridges. The org is
 * created by the idempotent SECURITY DEFINER RPC fn_provision_org.
 */
export function OnboardingCheck({ children }: { children: React.ReactNode }) {
    type BootStatus = 'checking' | 'booting' | 'ready';
    const [status, setStatus] = useState<BootStatus>('checking');
    const [provisioningComplete, setProvisioningComplete] = useState(false);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);
    const [businessName, setBusinessName] = useState<string | undefined>();
    const [userName, setUserName] = useState<string | undefined>();
    const [isFirstBoot, setIsFirstBoot] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [needsLegalAcceptance, setNeedsLegalAcceptance] = useState(false);
    const [legalAccepted, setLegalAccepted] = useState(false);
    const [scopeConfirmed, setScopeConfirmed] = useState(false);

    useEffect(() => {
        const handleBoot = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                const { data: acceptance, error } = await supabase.from('signup_legal_acceptances')
                    .select('user_id').eq('user_id', user.id).maybeSingle();
                if (error || !acceptance) {
                    setNeedsLegalAcceptance(true);
                    setStatus('ready');
                    return;
                }
            }
            const params = new URLSearchParams(window.location.search);
            const inviteToken = params.get('invite_token') || localStorage.getItem('pending_invite');
            if (inviteToken) {
                await processInvite(inviteToken);
                return;
            }
            await checkOrg();
        };
        handleBoot();
    }, []);

    // Invite path preserved (invited users join an existing org, skip setup).
    const processInvite = async (rawToken: string) => {
        let token = rawToken;
        const uuidMatch = rawToken.match(/[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}/i);
        if (uuidMatch) token = uuidMatch[0];
        const hexMatch = rawToken.match(/[0-9a-f]{32}/i);
        if (!uuidMatch && hexMatch) token = hexMatch[0];

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            localStorage.setItem('pending_invite', token);
            setStatus('ready');
            return;
        }

        try {
            const { data: details, error: detailsError } = await supabase.rpc('get_invite_details', { token_text: token });
            if (detailsError) reportError(detailsError, 'onboarding-check.get-invite-details');
            if (details && details.valid && details.email) {
                const inviteEmail = details.email.toLowerCase().trim();
                const userEmail = (user.email || '').toLowerCase().trim();
                if (inviteEmail !== userEmail) {
                    localStorage.setItem('pending_invite', token);
                    await supabase.auth.signOut();
                    await new Promise(r => setTimeout(r, 200));
                    window.location.href = `/?invite_token=${token}`;
                    return;
                }
            }
        } catch (_e) {
            // Could not verify invite details — proceed anyway
        }

        const { data, error } = await supabase.rpc('accept_invite', { token_text: token });
        if (!error && data && (data as any).success) {
            localStorage.removeItem('pending_invite');
            window.location.href = '/';
            return;
        }
        // The workshop's team seats are full (20261013220000). Say so, rather
        // than the generic "expired or invalid" below.
        const result = data as { error?: string; org_name?: string } | null;
        if (!error && result?.error === 'seat_limit') {
            setSubmitError(`${result.org_name || 'This workshop'} has no free team seats right now. Ask the owner to free a seat or add one, then open the invite link again.`);
            localStorage.removeItem('pending_invite');
            await checkOrg();
            return;
        }
        if (error) {
            setSubmitError('Invite link has expired or is invalid. Please ask for a new one.');
        }
        // Invite failed/expired — fall through to normal boot rather than
        // trapping the user behind a blocking alert loop.
        localStorage.removeItem('pending_invite');
        await checkOrg();
    };

    const checkOrg = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setStatus('ready'); return; }

        const { data: member, error: memberError } = await supabase
            .from('organization_members')
            .select('org_id, organizations ( name, vertical_id )')
            .eq('user_id', user.id)
            .single();
        if (memberError) reportError(memberError, 'onboarding-check.organization-members');

        const org: any = member?.organizations
            ? (Array.isArray(member.organizations) ? member.organizations[0] : member.organizations)
            : null;

        if (org && org.name && org.vertical_id) {
            // Existing org -> play the boot animation INTO the app.
            // Detect first-boot: flag is set right before the post-provisioning reload.
            const firstBoot = sessionStorage.getItem('fds_first_boot') === '1';
            if (firstBoot) sessionStorage.removeItem('fds_first_boot');
            setIsFirstBoot(firstBoot);
            setBusinessName(org.name);

            // Fetch user's personal name for sentient greetings
            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('full_name')
                .eq('id', user.id)
                .single();
            if (profileError) reportError(profileError, 'onboarding-check.profiles');
            if (profile?.full_name) {
                // Extract first name for a natural greeting
                setUserName(profile.full_name.split(' ')[0]);
            }

            setProvisioningComplete(true);
            setStatus('booting');
        } else {
            // No org membership -> straight to the onboarding step.
            // Never show SystemBoot first (that read as "the app flashing").
            setNeedsOnboarding(true);
        }
    };

    const submitOnboarding = async () => {
        const name = nameInput.trim();
        if (name.length < 2 || submitting || !scopeConfirmed) return;
        setSubmitting(true);
        setSubmitError(null);

        // Partner attribution. The code was captured on the landing URL, which
        // may have been a different day -- see lib/referral.ts. Passing null is
        // the normal case (organic signup) and the RPC treats an unknown or
        // expired code exactly like null, so a bad code can never block signup.
        const refCode = readRefCode();

        const { data: orgId, error } = await supabase.rpc('fn_provision_org', {
            p_org_name: name,
            p_vertical_id: 'mechanic',
            p_ref_code: refCode,
        });

        if (error || !orgId) {
            setSubmitError(error?.message || 'Could not set up your workshop. Please try again.');
            setSubmitting(false);
            return;
        }

        // Attribution is now recorded server-side and is write-once, so the
        // local copy has done its job. Clearing it stops a second org created
        // from the same browser -- a partner demoing signup, say -- inheriting
        // the credit.
        if (refCode) clearRefCode();

        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
            // This is the ONLY welcome send (the database no longer sends one,
            // 20260930130000), so it is awaited: the hard reload below would
            // otherwise cancel the request in flight. Bounded, so a slow edge
            // function cannot hold the trader on this screen, and reported,
            // because invoke() resolves an HTTP failure into `error` rather
            // than rejecting. Never blocking: the workshop already exists.
            const welcome = supabase.functions.invoke('send-email', {
                // org_id is required for send-email to authorise the caller as a
                // member of the org — without it the welcome email is 401'd.
                body: { type: 'welcome_email', app_url: getAppUrl(), data: { org_id: orgId, email: user.email, name } },
            });
            const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
            try {
                const result = await Promise.race([welcome, timeout]);
                if (result?.error) reportError(result.error, 'onboarding-check.send-email');
            } catch (welcomeError) {
                reportError(welcomeError, 'onboarding-check.send-email');
            }
        }

        // Flag this as a first boot so SystemBoot can show first-time messages.
        sessionStorage.setItem('fds_first_boot', '1');
        // Hard reload so all context providers pick up the new org.
        window.location.href = '/';
    };

    if (needsLegalAcceptance) {
        return <div className="fd-fullscreen fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: C.bg }}>
            <div className="w-full max-w-md rounded-[var(--fd-r-xl)] p-6 sm:p-7 space-y-4 shadow-lifted" style={{ background: C.surface, color: C.text, border: `1px solid ${C.border}` }}>
                <p className="fd-eyebrow">One last thing</p>
                <h1 className="text-2xl font-semibold tracking-tight">Before you continue</h1>
                <p className="text-sm" style={{ color: C.dim }}>Please review the terms for using ForgeDash and processing your customers' data.</p>
                <label className="flex items-start gap-3 text-sm leading-relaxed"><input type="checkbox" className="mt-1 w-4 h-4 shrink-0" checked={legalAccepted} onChange={e => setLegalAccepted(e.target.checked)} />
                    <span>I accept the <a className="underline" href="https://forgedash.uk/terms" target="_blank" rel="noopener noreferrer">Terms</a> and <a className="underline" href="https://forgedash.uk/dpa" target="_blank" rel="noopener noreferrer">DPA</a>. I have read the <a className="underline" href="https://forgedash.uk/privacy" target="_blank" rel="noopener noreferrer">Privacy Notice</a>.</span>
                </label>
                {submitError && <p className="text-sm text-danger">{submitError}</p>}
                <button disabled={!legalAccepted || submitting} className="fd-btn fd-btn-primary w-full" onClick={async () => {
                    setSubmitting(true);
                    const { error } = await supabase.rpc('fn_record_legal_acceptance');
                    if (error) { setSubmitError('Could not record acceptance. Please retry.'); setSubmitting(false); }
                    else window.location.reload();
                }}>Accept and continue</button>
            </div>
        </div>;
    }

    // ── Onboarding step (Forge Workshop) ──────────────────────────────────────
    if (needsOnboarding) {
        const valid = nameInput.trim().length >= 2 && scopeConfirmed;
        return (
            <div className="fd-fullscreen fixed inset-0 z-50 flex items-center justify-center p-6"
                style={{ background: C.bg }}>
                <div className="absolute inset-0 pointer-events-none"
                    style={{ background: `radial-gradient(800px 500px at 78% 14%, color-mix(in oklab, var(--fd-silver) 8%, transparent), transparent 60%)` }} />
                <div className="relative w-full max-w-md">
                    <p className="fd-eyebrow mb-3">Welcome to ForgeDash</p>
                    <h1 className="text-3xl font-semibold tracking-tight mb-2" style={{ color: C.text }}>
                        Let's set up your workshop.
                    </h1>
                    <p className="text-sm mb-7" style={{ color: C.dim }}>
                        ForgeDash supports garage businesses operated by sole traders in the UK. Choose your income tax region in Settings → Tax &amp; VAT before relying on tax estimates.
                    </p>

                    <div className="rounded-[var(--fd-r-xl)] p-6 shadow-lifted"
                        style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                        <label className="block text-xs font-mono uppercase tracking-wider mb-2"
                            style={{ color: C.dim }}>Business name</label>
                        <input
                            autoFocus
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && valid) submitOnboarding(); }}
                            placeholder="e.g. Apex Auto Works"
                            className="fd-input mb-6"
                        />

                        <label className="block text-xs font-mono uppercase tracking-wider mb-2"
                            style={{ color: C.dim }}>Your trade</label>
                        <div className="flex items-center gap-4 rounded-xl p-4 relative"
                            style={{
                                background: 'color-mix(in oklab, var(--fd-gold-fill) 7%, transparent)',
                                border: '1px solid color-mix(in oklab, var(--fd-gold) 45%, transparent)',
                                boxShadow: 'var(--fd-shadow-3)',
                            }}>
                            <div className="flex-none w-12 h-12 rounded-xl grid place-items-center"
                                style={{ background: C.accent }}>
                                <svg viewBox="0 0 24 24" width="24" height="24" fill="none"
                                    stroke={C.accentInk} strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-.6-.6-2.4 2.6-2.6Z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-bold" style={{ color: C.text }}>Mechanic &amp; Garage</h3>
                                <p className="text-xs" style={{ color: C.dim }}>MOT, repairs, parts, VRM lookup</p>
                            </div>
                            <span className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full"
                                style={{ color: C.accent, background: 'color-mix(in oklab, var(--fd-gold-fill) 12%, transparent)', border: '1px solid color-mix(in oklab, var(--fd-gold) 40%, transparent)' }}>
                                Your trade
                            </span>
                        </div>

                        {submitError && (
                            <p className="text-xs mt-4" style={{ color: 'var(--fd-danger)' }}>{submitError}</p>
                        )}

                        <label className="flex items-start gap-3 mt-4 text-sm leading-relaxed" style={{ color: C.dim }}>
                            <input type="checkbox" className="mt-1 w-4 h-4 shrink-0" checked={scopeConfirmed} onChange={e => setScopeConfirmed(e.target.checked)} />
                            <span>I operate this business as a sole trader in the UK. Tax estimates do not file my return.</span>
                        </label>
                        <button
                            onClick={submitOnboarding}
                            disabled={!valid || submitting}
                            className="fd-btn fd-btn-primary fd-btn-lg w-full mt-8">
                            {submitting
                                ? <><Loader2 className="w-4 h-4 animate-spin" /> Setting up…</>
                                : <>Enter ForgeDash <ArrowRight className="w-4 h-4" /></>}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Neutral on-brand splash while we resolve org state. Prevents both the
    // dashboard chrome AND SystemBoot from flashing before we know whether
    // this user needs onboarding.
    if (status === 'checking') {
        return (
            <div className="fd-fullscreen fixed inset-0 z-50 flex items-center justify-center"
                style={{ background: C.bg }}>
                <Loader2 className="w-7 h-7 animate-spin" style={{ color: C.accent }} />
            </div>
        );
    }

    if (status === 'booting') {
        return (
            <SystemBoot
                isReady={provisioningComplete}
                businessName={businessName}
                userName={userName}
                isFirstBoot={isFirstBoot}
                onComplete={() => setStatus('ready')}
            />
        );
    }

    return <>{children}</>;
}
